import * as React from "react";
import { Settings, Star, Receipt } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CreditKeyAccessPanel } from "@/components/internal/credit-key/CreditKeyAccessPanel";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { cn } from "@/lib/utils";

import { FinancialSettingsTab } from "./FinancialSettingsTab";
import { PointsTab } from "./PointsTab";
import { PaymentHistoryTab } from "./PaymentHistoryTab";

const subTabs = [
  { value: "settings", label: "Settings", icon: Settings },
  { value: "points", label: "Points", icon: Star },
  { value: "history", label: "Payment History", icon: Receipt },
] as const;

type SubTab = (typeof subTabs)[number]["value"];

export function CustomerAccountSection() {
  const users = useUsersForImpersonation();
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [activeSubTab, setActiveSubTab] = React.useState<SubTab>("settings");

  const filteredUsers = React.useMemo(() => {
    if (!users || !search) return [];
    const q = search.toLowerCase();
    return users
      .filter(
        (u: any) =>
          u.email?.toLowerCase().includes(q) ||
          u.name?.toLowerCase().includes(q) ||
          u.company?.toLowerCase().includes(q),
      )
      .slice(0, 20);
  }, [users, search]);

  const selectedUser = users?.find((u: any) => u._id === selectedUserId);

  return (
    <div className="space-y-6">
      {!selectedUserId ? (
        <Card>
          <CardHeader>
            <CardTitle>Customer Account</CardTitle>
            <CardDescription>
              Search for a customer to manage their financial settings, points,
              and payment history
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="Search by email, name, or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {search && filteredUsers.length > 0 ? (
              <div className="max-h-64 overflow-y-auto rounded border">
                {filteredUsers.map((u: any) => (
                  <button
                    key={u._id}
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted"
                    onClick={() => {
                      setSelectedUserId(u._id);
                      setSearch("");
                    }}
                  >
                    <div className="flex-1">
                      <p className="font-medium">{u.name || u.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {u.email}
                        {u.company ? ` · ${u.company}` : ""}
                      </p>
                    </div>
                    {u.isEnterprise ? (
                      <Badge
                        variant="outline"
                        className={getEnterpriseBadgeClass(true)}
                      >
                        Enterprise
                      </Badge>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}

            {search && filteredUsers.length === 0 && users ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No customers found
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* User Header */}
          <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
            <div className="flex-1">
              <p className="font-medium">
                {selectedUser?.name || selectedUser?.email}
              </p>
              <p className="text-sm text-muted-foreground">
                {selectedUser?.email}
                {selectedUser?.company
                  ? ` · ${selectedUser.company}`
                  : ""}
              </p>
            </div>
            <Badge
              variant="outline"
              className={getEnterpriseBadgeClass(selectedUser?.isEnterprise)}
            >
              {getEnterpriseBadgeLabel(selectedUser?.isEnterprise)}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedUserId("")}
            >
              Change
            </Button>
          </div>

          {/* Sub-tabs */}
          <div className="flex items-center gap-1 rounded-lg bg-muted p-[3px]">
            {subTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveSubTab(tab.value)}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-1 text-sm font-medium whitespace-nowrap transition-all",
                    activeSubTab === tab.value
                      ? "bg-background text-foreground border-transparent shadow-sm"
                      : "text-foreground/60 border-transparent hover:text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Sub-tab content */}
          {activeSubTab === "settings" ? (
            <div className="space-y-6">
              <FinancialSettingsTab userId={selectedUserId} />
              <CreditKeyAccessPanel key={selectedUserId} customerId={selectedUserId} />
            </div>
          ) : null}
          {activeSubTab === "points" ? (
            <PointsTab userId={selectedUserId} />
          ) : null}
          {activeSubTab === "history" ? (
            <PaymentHistoryTab userId={selectedUserId} />
          ) : null}
        </>
      )}
    </div>
  );
}
